import { useNavigate } from 'react-router-dom';
import { BookingStatusTag } from 'Views/Components/_HumanWritten/Bookings/BookingStatusTag';
import { Button, Colors, Display } from '../../../Button/Button';
import If from '../../../If/If';
import moment from 'moment/moment';
import { BookingSummary } from '../../BookingSummaryDto';
import { BookingEntity, FerryTripEntity } from 'Models/Entities';
import { bookingType } from 'Models/Enums';
import { FormattedMessage, useIntl } from 'react-intl';
import { FreightIcon } from '../../Icon/IconHelpers';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { useLocationNamesFromBookingSummary } from './Hooks/useLocationNamesFromBookingSummary';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import GuestTag from 'Views/Components/_HumanWritten/Tag/GuestTag';
import { ROUTES, WLIDS } from 'Constants';
import { store } from 'Models/Store';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import { PricingModalContent } from './Modals/PricingModalContent';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export interface BookingDetailedViewHeaderProps {
	booking: BookingEntity;
	bookedSummary: BookingSummary;
	typeOfBooking: bookingType;
}

export function BookingDetailedViewHeader({ booking, bookedSummary, typeOfBooking }: BookingDetailedViewHeaderProps) {
	const navigate = useNavigate();
	const intl = useIntl();

	const ferryTrip = new FerryTripEntity(bookedSummary.ferryTrip);

	const { startLocationName, endLocationName } = useLocationNamesFromBookingSummary(bookedSummary);

	const onCreateAccount = () => {
		navigate(ROUTES.CONVERT_GUEST, {
			state: {
				firstName: store.userData?.firstName,
				lastName: store.userData?.lastName,
				email: store.userData?.email,
				phone: store.userData?.phone,
				postcode: store.userData?.postcode,
			},
		});
	};

	return (
		<>
			{store.userData?.isGuest && (
				<div className="guest-section">
					<MessageBox title={intl.formatMessage(WLIDS.guest.createAccountHeader)} status="info">
						<p>
							<FormattedMessage {...WLIDS.guest.createAccountBody} />
						</p>
						<Button
							display={Display.Solid}
							colors={Colors.Secondary}
							onClick={onCreateAccount}
							newStyles
							buttonProps={{ 'data-test-id': 'booking-details-create-account-button' } as any}
						>
							<FormattedMessage {...WLIDS.actions.createAccount} />
						</Button>
					</MessageBox>
				</div>
			)}
			<div className="booking-detailed-view__header">
				{!store.userData?.isGuest && (
					<Button
						className="booking-detailed-view__back-btn"
						onClick={() => {
							const hasHistory = window.history.length > 1;
							if (hasHistory) {
								// TODO fix this (or it navigates back to booking wizard)
								navigate(-1);
							} else {
								navigate('/ferry-schedule');
							}
						}}
					>
						<Icon name="chevron_left" useMaterialIcons />
					</Button>
				)}
				<div className="booking-detailed-view__header-text">
					<If condition={!(typeOfBooking === 'FREIGHT' || typeOfBooking === 'EVENT_BOOKING')}>
						<h5 className="booking-detailed-view__title">
							{startLocationName} to {endLocationName}
						</h5>
					</If>
					<If condition={typeOfBooking === 'EVENT_BOOKING'}>
						<h5 className="booking-detailed-view__title event-booking__header">
							{ferryTrip?.getEventName()}
						</h5>
					</If>
					<If condition={typeOfBooking === 'FREIGHT'}>
						<h5 className="booking-detailed-view__title freight-booking__header">
							<FreightIcon className="freight-booking__icon" />
							{startLocationName} to {endLocationName}
						</h5>
					</If>
					<div className="booking-detailed-view__header-meta">
						<span className="booking-detailed-view__booking-id">#{booking.humanReadableId}</span>
						{(booking.transactionStatuses?.length
							? booking.transactionStatuses
							: [booking.transactionStatus]
						).map(status => (
							<BookingStatusTag key={status} status={status} isStaff={store.isStaff} />
						))}
						{booking.user?.isGuest && <GuestTag />}
					</div>
					<p className="booking-created-date">
						<FormattedMessage
							id="booking-details__created-date__label"
							defaultMessage="Booking created on {date}"
							values={{
								date: `${moment(booking.created).format('DD MMM YYYY')}, ${formatStandardTime(booking.created)}`,
							}}
						/>
					</p>
				</div>
				<div className="booking-detailed-view__header-price">
					{/* Super admins open the price breakdown by clicking the amount itself — the separate info
					    icon was removed in favour of making the number the affordance. Everyone else sees a
					    plain, non-interactive amount, so the button only exists when it does something. */}
					<If condition={store.isSuper}>
						<button
							type="button"
							className="booking-detailed-view__header-price-amount booking-detailed-view__header-price-amount--clickable"
							aria-label="Show pricing"
							onClick={() =>
								store.modal?.show(
									'Pricing',
									<PricingModalContent bookingId={booking.id} total={bookedSummary.totalCost} />,
									{ newModal: true, shouldCloseOnEsc: true, shouldCloseOnOverlayClick: true },
								)
							}
						>
							{formatPriceDisplay(bookedSummary.totalCost, false)}
						</button>
					</If>
					<If condition={!store.isSuper}>
						<span className="booking-detailed-view__header-price-amount">
							{formatPriceDisplay(bookedSummary.totalCost, false)}
						</span>
					</If>
					<If condition={bookedSummary.discountAmount > 0}>
						<span className="booking-detailed-view__header-price-saving">
							Discount -{formatPriceDisplay(bookedSummary.discountAmount, false)}
						</span>
					</If>
				</div>
			</div>
		</>
	);
}
